import {create} from 'zustand/react';
import type {ModalStackItem} from '../compoents/modal';

interface IGlobalStore {
    isLoading: boolean;
    setLoading: (val: boolean) => void;
    isFullScreen: boolean;
    setFullScreen: (val: boolean) => void;
    currentCursor: string;
    setCursor: (val: string) => void;
    stack: ModalStackItem[];
    baseZIndex: number;
    generateId: () => string;
    push: (item: Omit<ModalStackItem, 'zIndex'>) => string;
    pop: () => void;
    remove: (id: string) => void;
    clear: () => void;
    getTop: () => ModalStackItem | null;
    getById: (id: string) => ModalStackItem | null;
    isEmpty: () => boolean;
    isInStack: (val: string) => boolean;
    isTopItem: (val: string) => boolean;
}

const useGlobalStore = create<IGlobalStore>((set, get) => ({
    isLoading: false,
    setLoading: (val: boolean) => {
        set({isLoading: val});
    },

    isFullScreen: false,
    setFullScreen: (val: boolean) => {
        set({isFullScreen: val});
    },

    currentCursor: 'streaming',
    setCursor: (val: string) => {
        set({currentCursor: val});
    },

    stack: [],
    baseZIndex: 1000,

    generateId: () => Math.random().toString(36).substring(2, 9),

    push: (item) => {
        const id = item.id;
        const currentStack = get().stack;
        const zIndex = get().baseZIndex + currentStack.length;
        const newItem = {
            ...item,
            id,
            zIndex,
        } as ModalStackItem;
        set((state) => ({
            stack: [...state.stack, newItem],
        }));
        return id;
    },

    pop: () => {
        set((state) => {
            if (state.stack.length === 0) return state;
            const topItem = state.stack[state.stack.length - 1];
            if (topItem.onClose) {
                topItem.onClose();
            }
            return {
                stack: state.stack.slice(0, -1),
            };
        });
    },

    remove: (id) => {
        set((state) => {
            const item = state.stack.find((item) => item.id === id);
            if (item?.onClose) {
                item.onClose();
            }
            return {
                stack: state.stack.filter((item) => item.id !== id),
            };
        });
    },

    clear: () => {
        const currentStack = get().stack;
        currentStack.forEach((item) => {
            if (item.onClose) {
                item.onClose();
            }
        });
        set({stack: []});
    },

    getTop: () => {
        const stack = get().stack;
        return stack.length > 0 ? stack[stack.length - 1] : null;
    },

    getById: (id) => {
        return get().stack.find((item) => item.id === id) || null;
    },

    isEmpty: () => {
        return get().stack.length === 0;
    },

    isInStack: (id: string) => {
        return get().stack.some((item) => item.id === id);
    },

    isTopItem: (id: string) => {
        const stack = get().stack;
        const topId = stack.length > 0 ? stack[stack.length - 1].id : null;
        return topId === id;
    },
}));

export default useGlobalStore;
